<script setup lang="ts">
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Popover from '@uiKit/elements/Popover.vue'
import { BuildingIcon } from '@uiKit/icons'
import { formatDayMonthYear, formatDaysAgo } from '@shared/utils'
import PropertyStatusChip from './PropertyStatusChip.vue'
import { propertyTypeLabelOf } from '../../constants/propertyLifecycle'
import { formatStatementMoney } from '../../utils/settlementMoneyUtils'
import type { PropertySummary } from '../../types/propertyTypes'

// One row of the Properties table, drawn like a Sell Requests row so the two
// lists read as one product. Presentational — emits `view` with the id; the whole
// row opens the property, and ⋮ › View details does the same.
//
// No funding figures: progress lives on the Marketplace (FSD 3.3.1). The column
// template comes from the owning table (`--list-table-columns`); below 992px the
// cells regroup into a card, each value gaining its visually-hidden label.
const props = defineProps<{ property: PropertySummary }>()
const emit = defineEmits<{ view: [id: string] }>()

const propertyType = computed(() => propertyTypeLabelOf(props.property.propertyType))
const location = computed(() =>
  [props.property.areaName, props.property.city].filter(Boolean).join(', '),
)

function moneyOf(value: string | null): string {
  return formatStatementMoney(props.property.currencyCode, value)
}

const approvedOn = computed(() => formatDayMonthYear(props.property.promotedAt))
const approvedAgo = computed(() => formatDaysAgo(props.property.promotedAt))

function view() {
  emit('view', props.property.id)
}

const actionsMenu = ref<InstanceType<typeof Popover> | null>(null)
const isMenuOpen = ref(false)

function toggleMenu(event: MouseEvent) {
  actionsMenu.value?.toggle(event)
}

function viewFromMenu() {
  actionsMenu.value?.hide()
  view()
}
</script>

<template>
  <Box is="li" class="property-row" @click="view">
    <Box flex align="center" gap="3" class="property-row__cell property-row__cell--property">
      <Box flex align="center" justify="center" class="property-row__icon" aria-hidden="true">
        <BuildingIcon width="20" height="20" />
      </Box>
      <Box direction="col" gap="1" class="property-row__identity">
        <Text is="h3" type="body" weight="semibold" truncate>{{ property.propertyName }}</Text>
        <Box flex align="center" gap="2" class="property-row__meta">
          <Text v-if="propertyType" is="span" type="caption-sm" class="property-row__type">
            {{ propertyType }}
          </Text>
          <Text
            v-if="property.propertyRefCode"
            is="span"
            type="caption"
            color="var(--p-sliceal-text-subtle)"
            truncate
          >
            {{ property.propertyRefCode }}
          </Text>
        </Box>
      </Box>
    </Box>

    <Box direction="col" gap="1" class="property-row__cell property-row__cell--location">
      <Text type="micro" class="property-row__label">Location</Text>
      <Text type="body-sm" weight="medium" truncate>{{ location || '—' }}</Text>
    </Box>

    <Box direction="col" gap="1" class="property-row__cell property-row__cell--asking">
      <Text type="micro" class="property-row__label">Asking price</Text>
      <Text type="body" weight="semibold" class="property-row__nowrap">
        {{ moneyOf(property.askingPrice) }}
      </Text>
    </Box>

    <Box direction="col" gap="1" class="property-row__cell property-row__cell--agreed">
      <Text type="micro" class="property-row__label">Agreed price</Text>
      <Text type="body" weight="semibold" class="property-row__nowrap">
        {{ moneyOf(property.priceAgreed) }}
      </Text>
    </Box>

    <Box direction="col" gap="1" class="property-row__cell property-row__cell--status">
      <Text type="micro" class="property-row__label">Status</Text>
      <Box flex>
        <PropertyStatusChip :property="property" size="small" />
      </Box>
    </Box>

    <Box direction="col" gap="1" class="property-row__cell property-row__cell--approved">
      <Text type="micro" class="property-row__label">Approved for setup</Text>
      <Text type="body-sm" weight="medium" class="property-row__nowrap">{{ approvedOn }}</Text>
      <Text v-if="approvedAgo" type="caption" color="var(--p-sliceal-text-subtle)">
        {{ approvedAgo }}
      </Text>
    </Box>

    <Box flex justify="end" class="property-row__cell property-row__cell--action">
      <Button
        variant="text"
        color="secondary"
        icon="ellipsis-v"
        size="small"
        class="property-row__action"
        :aria-label="`More actions for ${property.propertyName}`"
        aria-haspopup="true"
        :aria-expanded="isMenuOpen"
        @click.stop="toggleMenu"
      />
      <Popover ref="actionsMenu" @show="isMenuOpen = true" @hide="isMenuOpen = false">
        <Box direction="col" class="property-row__menu" role="menu">
          <Button
            label="View details"
            icon="eye"
            variant="text"
            color="secondary"
            size="small"
            role="menuitem"
            class="property-row__menu-item"
            @click.stop="viewFromMenu"
          />
        </Box>
      </Popover>
    </Box>
  </Box>
</template>

<style scoped>
/* Anchors the hidden cell labels here — unanchored, they stretch the page. */
.property-row {
  position: relative;
  display: grid;
  grid-template-columns: var(--list-table-columns);
  align-items: center;
  column-gap: 1rem;
  padding: 0.9375rem 1.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  cursor: pointer;
  line-height: normal;
  transition: background-color 0.15s ease;
}

.property-row:last-child {
  border-bottom: none;
}

.property-row:hover {
  background: var(--p-surface-50);
}

.property-row__cell,
.property-row__identity,
.property-row__meta {
  min-width: 0;
}

.property-row__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--p-primary-50);
  color: var(--p-primary-500);
}

.property-row__type {
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--p-surface-100);
  color: var(--p-surface-600);
}

.property-row__nowrap {
  white-space: nowrap;
}

/* The table's header row names each column, so per-cell labels are visually
   hidden but still read aloud; the stacked card below 992px shows them. */
.property-row__label {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.property-row__action {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.property-row__menu {
  padding: 0.25rem;
  min-width: 180px;
}

.property-row__menu-item {
  justify-content: flex-start;
  width: 100%;
}

@media (max-width: 992px) {
  .property-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      'property property'
      'location status'
      'asking agreed'
      'approved approved';
    row-gap: 1rem;
    padding: 1rem;
  }

  .property-row__cell--property {
    grid-area: property;
    padding-right: 3rem;
  }

  .property-row__cell--location {
    grid-area: location;
  }

  .property-row__cell--asking {
    grid-area: asking;
  }

  .property-row__cell--agreed {
    grid-area: agreed;
  }

  .property-row__cell--status {
    grid-area: status;
  }

  .property-row__cell--approved {
    grid-area: approved;
  }

  .property-row__cell--action {
    position: absolute;
    top: 0.75rem;
    right: 0.5rem;
  }

  .property-row__label {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    overflow: visible;
    clip-path: none;
  }

  .property-row__action {
    width: 44px;
    height: 44px;
  }

  .property-row__nowrap {
    white-space: normal;
  }
}
</style>
